import React from "react";
import { Link } from "react-router-dom";
import { Instagram, Twitter, Youtube, Mail, MapPin, Phone } from "lucide-react";
import { useSiteContent } from "@/lib/siteContent";

export function Footer() {
  const year = new Date().getFullYear();
  const { site_settings: s, footer_links: links } = useSiteContent();

  const socialLinks = [
    { icon: Instagram, label: "Instagram", href: s.social_instagram },
    { icon: Twitter, label: "Twitter", href: s.social_twitter },
    { icon: Youtube, label: "YouTube", href: s.social_youtube },
  ].filter((sl) => sl.href && sl.href !== "");

  // Render either an internal Link or an external anchor based on the href
  const renderLink = (item, testid) => {
    const href = item.href || "#";
    const isInternal = href.startsWith("/") && !href.startsWith("//");
    if (isInternal) {
      return (
        <Link to={href} data-testid={testid} className="hover:text-[#C5A059]">
          {item.label}
        </Link>
      );
    }
    return (
      <a href={href} data-testid={testid} className="hover:text-[#C5A059]">
        {item.label}
      </a>
    );
  };

  const columns = [
    { key: "explore", title: "Explore", items: links.explore || [] },
    { key: "society", title: "Society", items: links.society || [] },
    { key: "support", title: "Support", items: links.support || [] },
    { key: "company", title: "Company", items: links.company || [] },
  ];

  return (
    <footer
      data-testid="global-footer"
      className="mt-16 sm:mt-24 border-t border-black/10 bg-[#F1ECE4]/40 sd-noise"
    >
      {/* ---- Newsletter strip ---- */}
      <div className="border-b border-black/10 bg-[#FAF8F5]/60">
        <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-10 py-8 sm:py-10 grid gap-6 lg:grid-cols-[1.2fr_1fr] items-center">
          <div>
            <div className="text-[10px] uppercase tracking-[0.24em] text-[#C5A059] mb-2">{s.newsletter_eyebrow}</div>
            <h3 className="font-serif text-2xl sm:text-3xl text-[#1A1918] leading-[1.1]">
              {s.newsletter_headline}
            </h3>
          </div>
          <form
            className="flex flex-col sm:flex-row gap-2"
            onSubmit={(e) => e.preventDefault()}
            data-testid="footer-newsletter-form"
          >
            <input
              type="email"
              placeholder="Enter your email"
              required
              data-testid="footer-newsletter-input"
              className="flex-1 min-w-0 px-4 py-3.5 rounded-xl bg-[#FAF8F5] border border-black/10 text-sm focus:outline-none focus:border-[#C5A059]"
            />
            <button
              type="submit"
              data-testid="footer-newsletter-submit"
              className="whitespace-nowrap bg-[#1A1918] text-[#FAF8F5] px-6 py-3.5 rounded-xl text-xs uppercase tracking-[0.18em] hover:bg-[#C5A059] hover:text-[#0B0A08] transition-colors"
            >
              Subscribe
            </button>
          </form>
        </div>
      </div>

      {/* ---- Main link columns ---- */}
      <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-10 py-14 grid grid-cols-2 md:grid-cols-6 gap-8 lg:gap-10">
        <div className="col-span-2 md:col-span-2">
          <div className="flex items-center gap-3">
            <img
              src={s.logo_small_url || "/scentodore-logo-small.webp"}
              alt={s.brand_name || "Scentodore"}
              className="h-10 w-auto select-none pointer-events-none"
              draggable={false}
            />
            <div className="font-serif text-xl tracking-[0.14em] uppercase text-[#1A1918]">
              {s.brand_name}
            </div>
          </div>
          <p className="mt-4 text-sm text-[#1A1918]/70 leading-relaxed max-w-xs">
            {s.footer_description}
          </p>

          <ul className="mt-6 space-y-2 text-sm text-[#1A1918]/75">
            {s.address && (
              <li className="flex items-start gap-2">
                <MapPin className="h-3.5 w-3.5 mt-0.5 text-[#C5A059]" />
                <span data-testid="footer-address">{s.address}</span>
              </li>
            )}
            {s.email && (
              <li className="flex items-start gap-2">
                <Mail className="h-3.5 w-3.5 mt-0.5 text-[#C5A059]" />
                <a href={`mailto:${s.email}`} className="hover:text-[#C5A059]" data-testid="footer-email">
                  {s.email}
                </a>
              </li>
            )}
            {s.phone && (
              <li className="flex items-start gap-2">
                <Phone className="h-3.5 w-3.5 mt-0.5 text-[#C5A059]" />
                <a href={`tel:${s.phone.replace(/[^\d+]/g, "")}`} className="hover:text-[#C5A059]" data-testid="footer-phone">
                  {s.phone}
                </a>
              </li>
            )}
          </ul>

          {socialLinks.length > 0 && (
            <div className="mt-6 flex items-center gap-3">
              {socialLinks.map(({ icon: Icon, label, href }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noreferrer noopener"
                  aria-label={label}
                  data-testid={`footer-social-${label.toLowerCase()}`}
                  className="h-9 w-9 rounded-full border border-black/15 flex items-center justify-center text-[#1A1918]/70 hover:text-[#C5A059] hover:border-[#C5A059]/60 transition-colors"
                >
                  <Icon className="h-4 w-4" />
                </a>
              ))}
            </div>
          )}
        </div>

        {columns.map((col) => (
          <div key={col.key}>
            <div className="text-xs uppercase tracking-[0.18em] text-[#1A1918]/60">{col.title}</div>
            <ul className="mt-4 space-y-2.5 text-sm">
              {col.items.map((item, i) => (
                <li key={`${col.key}-${item.label}-${i}`}>
                  {renderLink(item, `footer-link-${col.key}-${i}`)}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      <div className="border-t border-black/10">
        <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-10 py-5 flex flex-col md:flex-row md:items-center md:justify-between gap-4 text-xs text-[#1A1918]/60">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            <span>© {year} {s.brand_name}. Crafted in small batches.</span>
            <span className="hidden md:inline text-[#1A1918]/30">·</span>
            {(links.policies || []).map((p, i) => {
              const href = p.href || "#";
              const isInternal = href.startsWith("/") && !href.startsWith("//");
              return isInternal ? (
                <Link key={`policy-${i}`} to={href} className="hover:text-[#C5A059]">{p.label}</Link>
              ) : (
                <a key={`policy-${i}`} href={href} className="hover:text-[#C5A059]">{p.label}</a>
              );
            })}
          </div>
          <div className="flex items-center gap-2 opacity-80">
            <span className="uppercase tracking-[0.18em] text-[10px]">We accept</span>
            {[
              { label: "Visa" },
              { label: "MC" },
              { label: "Amex" },
              { label: "Pay" },
              { label: "GPay" },
            ].map((p) => (
              <span
                key={p.label}
                className="text-[10px] uppercase tracking-[0.14em] px-2 py-1 rounded-md bg-[#FAF8F5] border border-black/10"
              >
                {p.label}
              </span>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}

export default Footer;
